// ./src/pages/About/index.js
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const About = () => {
  return (
    <div className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Company Overview */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About DeltaOps</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            DeltaOps is a cutting-edge change tracking system designed to help engineering teams identify the root causes of incidents swiftly and efficiently. By focusing on metadata about changes, DeltaOps ensures your source code remains secure while providing invaluable insights to enhance your incident management processes.
          </p>
        </div>

        {/* Our Mission and Vision */}
        <div className="mb-16 grid md:grid-cols-2 gap-8">
          {/* Mission */}
          <div>
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-gray-600">
              To empower engineering teams with the tools they need to swiftly diagnose and resolve incidents, minimizing downtime and maximizing productivity through intelligent change tracking and semantic search capabilities.
            </p>
          </div>
          {/* Vision */}
          <div>
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">Our Vision</h2>
            <p className="text-gray-600">
              To become the industry standard for incident management by continuously innovating and adapting to the evolving needs of modern engineering teams, ensuring that resolving issues is always a streamlined and efficient process.
            </p>
          </div>
        </div>

        {/* Our Solution */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Our Solution</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Problem Statement */}
            <div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">The Challenge</h3>
              <p className="text-gray-600 mb-6">
                In dynamic development environments, incidents can arise from a myriad of changes—deployments, configurations, migrations, and more. Identifying the exact change that triggered an incident is often time-consuming and resource-intensive, leading to prolonged downtimes and increased costs.
              </p>
            </div>
            {/* Our Approach */}
            <div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Our Approach</h3>
              <p className="text-gray-600 mb-6">
                DeltaOps leverages semantic search and intelligent metadata analysis to streamline the incident resolution process. By indexing detailed information about each change, DeltaOps enables teams to swiftly pinpoint the root cause of incidents without sifting through extensive logs or code histories.
              </p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Semantic Search</h3>
              <p className="text-gray-600">
                Quickly search through change metadata using natural language queries, making it easier to find relevant incidents.
              </p>
            </div>
            {/* Feature 2 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Secure Metadata Storage</h3>
              <p className="text-gray-600">
                We prioritize your code's security by only storing metadata about changes, ensuring your proprietary information remains protected.
              </p>
            </div>
            {/* Feature 3 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Seamless Integrations</h3>
              <p className="text-gray-600">
                DeltaOps integrates effortlessly with your existing tools and workflows, enhancing your incident management process without disruption.
              </p>
            </div>
            {/* Feature 4 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Real-Time Analytics</h3>
              <p className="text-gray-600">
                Gain actionable insights with our real-time analytics, helping you understand patterns, improve processes, and prevent future incidents.
              </p>
            </div>
            {/* Feature 5 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Customizable Dashboards</h3>
              <p className="text-gray-600">
                Tailor your dashboards to display the metrics and information that matter most to your team.
              </p>
            </div>
            {/* Feature 6 */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-medium text-gray-900 mb-3">Automated Alerts</h3>
              <p className="text-gray-600">
                Receive instant notifications about critical changes and incidents, ensuring timely responses.
              </p>
            </div>
          </div>
        </div>

        {/* Technology and Innovation */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Technology and Innovation</h2>
          <p className="text-gray-600 mb-6 text-center">
            At DeltaOps, we harness the power of advanced AI and machine learning algorithms to deliver precise and efficient incident analysis. Our platform continuously evolves, incorporating the latest technological advancements to meet the dynamic needs of modern engineering teams.
          </p>
          <p className="text-gray-600 text-center">
            Committed to innovation, we strive to provide tools that not only solve today's challenges but also anticipate tomorrow's needs.
          </p>
        </div>

        {/* Future Goals */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Looking Ahead</h2>
          <p className="text-gray-600 mb-6 text-center">
            As we continue to grow, our focus remains on enhancing DeltaOps.app to better serve engineering teams worldwide. Future developments include expanding our integration capabilities, introducing more advanced analytics features, and continuously refining our semantic search to deliver even more accurate and actionable insights.
          </p>
          <p className="text-gray-600 text-center">
            Our commitment to excellence drives us to innovate relentlessly, ensuring that DeltaOps.app remains at the forefront of incident management solutions.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Button 
            size="lg"
            className="bg-indigo-600 text-white hover:bg-indigo-700 px-8 py-4 rounded-full shadow-lg flex items-center justify-center"
            onClick={() => window.location.href = '/pricing'}
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default About;
